import { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, X } from 'lucide-react';
import { useTour } from '../contexts/TourContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'konnect_tour_chip_dismissed';

/**
 * Small "Tour 8/14 — Resume" chip that appears in the top bar whenever the
 * user has started but not finished the onboarding tour, and hasn't
 * dismissed this chip for the current session. Reads once on mount and
 * re-polls every 30s so it disappears naturally when the tour completes.
 */
export default function TourProgressChip() {
  const { active, startTour } = useTour();
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  const fetchProgress = async () => {
    try {
      // /api/tour/steps already returns current_step, completed, total
      const { data } = await axios.get(`${API}/api/tour/steps`);
      setProgress({
        current_step: data.current_step || 0,
        total_steps: data.total || 14,
        completed: !!data.completed,
      });
    } catch {
      setProgress(null);
    }
  };

  useEffect(() => {
    fetchProgress();
    const t = setInterval(fetchProgress, 30000);
    return () => clearInterval(t);
  }, []);

  // Re-fetch when tour opens/closes so the chip reflects the latest step
  useEffect(() => { if (!active) fetchProgress(); }, [active]);

  if (!progress || active || dismissed) return null;
  const { current_step = 0, total_steps = 14, completed = false } = progress;
  // Only show if in progress (step > 0, not complete)
  if (completed || current_step <= 0 || current_step >= total_steps) return null;

  const pct = Math.round((current_step / total_steps) * 100);

  return (
    <div
      className="hidden sm:flex items-center gap-2 pl-2 pr-1 py-1 rounded-full bg-gradient-to-r from-emerald-50 to-sky-50 border border-emerald-200 text-xs shadow-sm hover:shadow-md transition-all animate-fadeIn"
      data-testid="tour-progress-chip"
    >
      <button
        onClick={() => startTour({ resume: true })}
        className="flex items-center gap-1.5 cursor-pointer group"
        data-testid="resume-tour-btn"
      >
        <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 group-hover:bg-emerald-600">
          <Play className="h-2.5 w-2.5 text-white fill-white" />
        </span>
        <span className="font-medium text-emerald-700">Tour {current_step}/{total_steps}</span>
        <span className="w-14 h-1.5 bg-emerald-100 rounded-full overflow-hidden hidden md:inline-block">
          <span className="block h-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </span>
        <span className="text-emerald-600 group-hover:text-emerald-700 font-semibold">Resume</span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setDismissed(true);
          try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch {}
        }}
        className="p-0.5 rounded-full hover:bg-emerald-100 text-emerald-500 hover:text-emerald-700 transition"
        title="Dismiss for this session"
        data-testid="dismiss-tour-chip"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
